import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import type { FriendSurveyResponse } from '../../../types/businessPlan';

interface FriendSurveyProps {
  initialResponses?: FriendSurveyResponse[];
  onSave: (responses: FriendSurveyResponse[]) => void;
}

const EMPTY_RESPONSE: FriendSurveyResponse = {
  reaction: '',
  storeResponse: '',
  solvesProblem: '',
  whenUse: '',
  alternative: '',
};

const REACTION_OPTIONS = [
  '😍 "That\'s amazing! I love it!"',
  '🤔 "Interesting, tell me more"',
  '😐 "I\'m not sure about this..."',
  '😕 "I don\'t really get it"',
  '😬 "Honestly, I don\'t think it\'ll work"',
];

const SOLVES_PROBLEM_OPTIONS = [
  '✅ "Absolutely, it\'s a real pain point"',
  '👍 "Yeah, I think so"',
  '🤷 "I\'m not really sure"',
  '👎 "Probably not a big deal"',
  '❌ "No, this isn\'t really a problem"',
];

const WHEN_USE_OPTIONS = [
  '🚀 "Right away! I need this now"',
  '📅 "Within the next few months"',
  '⏰ "Maybe sometime in the future"',
  '🤔 "Only in very specific situations"',
  '🚫 "Honestly, I probably wouldn\'t use it"',
];

const FRIEND_COUNT = 5;

export default function FriendSurvey({ initialResponses = [], onSave }: FriendSurveyProps) {
  const [responses, setResponses] = useState<FriendSurveyResponse[]>(() => {
    const filled = [...initialResponses];
    while (filled.length < FRIEND_COUNT) {
      filled.push({ ...EMPTY_RESPONSE });
    }
    return filled;
  });
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (initialResponses.length > 0) {
      const filled = [...initialResponses];
      while (filled.length < FRIEND_COUNT) {
        filled.push({ ...EMPTY_RESPONSE });
      }
      setResponses(filled);
    }
  }, [initialResponses]);

  const updateField = (
    friendIdx: number,
    field: keyof FriendSurveyResponse,
    value: string,
  ) => {
    const updated = responses.map((r, i) =>
      i === friendIdx ? { ...r, [field]: value } : r,
    );
    setResponses(updated);
    onSave(updated);
  };

  const isFriendComplete = (r: FriendSurveyResponse) =>
    r.reaction !== '' && r.storeResponse !== '' && r.solvesProblem !== '' && r.whenUse !== '' && r.alternative !== '';

  const current = responses[activeTab];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto max-w-lg"
    >
      <h2 className="text-center text-2xl font-bold text-gray-900">Friend Survey</h2>
      <p className="mt-1 text-center text-sm text-gray-500">
        Ask 5 friends about your idea (optional)
      </p>

      {/* Tabs */}
      <div className="mt-4 flex gap-1.5 overflow-x-auto pb-2">
        {responses.map((r, idx) => (
          <button
            key={idx}
            onClick={() => setActiveTab(idx)}
            className={`flex min-w-[72px] flex-shrink-0 items-center justify-center gap-1 rounded-full px-3 py-2 text-sm font-medium transition-all ${
              activeTab === idx
                ? 'bg-brand-500 text-white'
                : isFriendComplete(r)
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {isFriendComplete(r) && activeTab !== idx && <span>✓</span>}
            Person {idx + 1}
          </button>
        ))}
      </div>

      {/* Survey form for active friend */}
      <div className="mt-4 space-y-5 rounded-2xl border-2 border-gray-200 bg-white p-5">
        {/* Q1: Reaction */}
        <div>
          <label className="text-sm font-bold text-gray-800">
            What was your first reaction?
          </label>
          <div className="mt-2 flex flex-col gap-2">
            {REACTION_OPTIONS.map((opt) => (
              <button
                key={opt}
                onClick={() => updateField(activeTab, 'reaction', opt)}
                className={`rounded-lg border-2 px-3 py-2 text-left text-sm transition-all ${
                  current.reaction === opt
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Q2: Store response */}
        <div>
          <label className="text-sm font-bold text-gray-800">
            If you saw this in a store/online, what&apos;s the first thing you&apos;d say?
          </label>
          <textarea
            value={current.storeResponse}
            onChange={(e) => updateField(activeTab, 'storeResponse', e.target.value)}
            placeholder="Type their response..."
            rows={2}
            className="mt-2 w-full resize-none rounded-lg border-2 border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-300 focus:border-brand-500 focus:outline-none"
          />
        </div>

        {/* Q3: Solves problem */}
        <div>
          <label className="text-sm font-bold text-gray-800">
            Does my business idea solve a real problem?
          </label>
          <div className="mt-2 flex flex-col gap-2">
            {SOLVES_PROBLEM_OPTIONS.map((opt) => (
              <button
                key={opt}
                onClick={() => updateField(activeTab, 'solvesProblem', opt)}
                className={`rounded-lg border-2 px-3 py-2 text-left text-sm transition-all ${
                  current.solvesProblem === opt
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Q4: When use */}
        <div>
          <label className="text-sm font-bold text-gray-800">
            When would you actually use this?
          </label>
          <div className="mt-2 flex flex-col gap-2">
            {WHEN_USE_OPTIONS.map((opt) => (
              <button
                key={opt}
                onClick={() => updateField(activeTab, 'whenUse', opt)}
                className={`rounded-lg border-2 px-3 py-2 text-left text-sm transition-all ${
                  current.whenUse === opt
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Q5: Alternative */}
        <div>
          <label className="text-sm font-bold text-gray-800">
            You&apos;ve only got enough $$ for ONE pick. What could you buy instead?
          </label>
          <textarea
            value={current.alternative}
            onChange={(e) => updateField(activeTab, 'alternative', e.target.value)}
            placeholder="What else could they buy?"
            rows={2}
            className="mt-2 w-full resize-none rounded-lg border-2 border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-300 focus:border-brand-500 focus:outline-none"
          />
        </div>
      </div>
    </motion.div>
  );
}
